import fm from "front-matter";

/**
 * Press release content loader.
 *
 * Each release is a markdown file in this folder (`*.md`) with YAML frontmatter.
 * Files are read at build time via Vite's `import.meta.glob` (raw string import),
 * parsed with the browser-safe `front-matter` package, and exposed as a typed,
 * date-sorted array. Add a release by dropping in a new `.md` file — no code
 * changes. See `_template.md` for the frontmatter format.
 */

/** One press release, parsed from a markdown file in this folder. */
export type PressRelease = {
  /** URL slug → /press/<slug>. Defaults to the filename (without `.md`). */
  slug: string;
  title: string;
  /** Dateline city, e.g. "San Diego". */
  city: string;
  /** ISO date, YYYY-MM-DD. Used for sorting and SEO. */
  date: string;
  /** Editorial category, e.g. "Product", "Company", "Privacy". */
  category: string;
  /** One-line summary — used in listings, the featured standfirst, and SEO. */
  summary: string;
  /** Marks the single featured lead story. */
  featured: boolean;
  /** Draft releases are excluded from the published list and detail routes. */
  draft: boolean;
  /** Optional external URL — links out instead of to the detail page. */
  externalUrl?: string;
  /**
   * Optional search-only overrides for the page's <title> and meta description
   * (and their Open Graph / X mirrors). They never change the displayed
   * headline, summary or the NewsArticle JSON-LD, which keep describing the
   * release as published. Without them the page uses "<title> — Compaire" and
   * the summary.
   */
  seoTitle?: string;
  seoDescription?: string;
  /** Markdown body (everything below the frontmatter). */
  body: string;
};

/** Shape of the YAML frontmatter as authored — every field optional. */
type PressFrontmatter = {
  slug?: string;
  title?: string;
  city?: string;
  // js-yaml parses unquoted ISO dates into Date objects, so allow both.
  date?: string | Date;
  category?: string;
  summary?: string;
  featured?: boolean;
  draft?: boolean;
  externalUrl?: string;
  seoTitle?: string;
  seoDescription?: string;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format an ISO date (YYYY-MM-DD) as "Aug 4, 2026" without timezone drift. */
export function formatPressDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  const [, y, mo, d] = m;
  return `${MONTHS[Number(mo) - 1]} ${Number(d)}, ${y}`;
}

/** Normalize a frontmatter date (string or js-yaml Date) back to YYYY-MM-DD. */
function toISODate(value: unknown): string {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return value ? String(value) : "";
}

function filenameSlug(path: string): string {
  return (path.split("/").pop() || "").replace(/\.md$/, "");
}

// Eager raw import of every markdown file in this folder. `?raw` returns the
// file contents as a string — no markdown loader/plugin required.
const files = import.meta.glob("/src/content/press/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

const all: PressRelease[] = Object.entries(files).map(([path, raw]) => {
  const { attributes, body } = fm<PressFrontmatter>(raw);
  return {
    slug: attributes.slug || filenameSlug(path),
    title: attributes.title ?? "",
    city: attributes.city ?? "",
    date: toISODate(attributes.date),
    category: attributes.category ?? "",
    summary: attributes.summary ?? "",
    featured: Boolean(attributes.featured),
    draft: Boolean(attributes.draft),
    externalUrl: attributes.externalUrl || undefined,
    seoTitle: attributes.seoTitle?.trim() || undefined,
    seoDescription: attributes.seoDescription?.trim() || undefined,
    body: (body || "").trim(),
  };
});

/** Published releases (drafts excluded), newest first. */
export const pressReleases: PressRelease[] = all
  .filter((r) => !r.draft)
  .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

/** The featured lead story: the newest release with `featured: true`, or null. */
export const featuredRelease: PressRelease | null =
  pressReleases.find((r) => r.featured) ?? null;

/** Published releases excluding the featured lead, newest first. */
export const listedReleases: PressRelease[] = pressReleases.filter(
  (r) => r.slug !== featuredRelease?.slug,
);

/** Look up a single published release by slug (drafts are not resolvable). */
export function getPressRelease(slug: string | undefined): PressRelease | undefined {
  if (!slug) return undefined;
  return pressReleases.find((r) => r.slug === slug);
}

/**
 * Search copy for a release page: the optional `seoTitle` / `seoDescription`
 * overrides when present, otherwise "<title> — Compaire" and the summary.
 */
export function pressReleaseSeo(release: Pick<PressRelease, "title" | "summary" | "seoTitle" | "seoDescription">): {
  title: string;
  description: string;
} {
  return {
    title: release.seoTitle ?? `${release.title} — Compaire`,
    description: release.seoDescription ?? release.summary,
  };
}
